/* eslint-disable max-len */
/**
 * @fileoverview Refactored Scheduled Posts List.
 *               PATH: src/components/ScheduledPostsList.js
 *               FIXED: Dark Theme & Sorting
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from '../../firebaseConfig';
import { FiLoader, FiZap, FiRefreshCw, FiPlay } from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import DashboardPostCard from '../common/DashboardPostCard';

const generateImageFromPromptFn = httpsCallable(functions, 'generateImageFromPrompt');
const cancelPostFn = httpsCallable(functions, 'cancelPost');
const postNowFn = httpsCallable(functions, 'postContentNow', { timeout: 300000 });

function ScheduledPostsList({ userId, projectId, recurringCampaigns, loadingCampaigns, onOpenEditModal }) {
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();
    const [allPosts, setAllPosts] = useState([]);
    const [loadingPosts, setLoadingPosts] = useState(true);
    const [error, setError] = useState('');
    const [processingPostId, setProcessingPostId] = useState(null);
    const [isBatchGenerating, setIsBatchGenerating] = useState(false);
    
    // Filters
    const [activeTab, setActiveTab] = useState('scheduled'); // 'scheduled' | 'drafts'
    const [campaignFilter, setCampaignFilter] = useState('all');

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // --- 1. Data Fetching ---
    useEffect(() => {
        if (!userId || !projectId) {
            if (isMountedRef.current) { setLoadingPosts(false); }
            return;
        }
        if (isMountedRef.current) { setLoadingPosts(true); setError(''); }

        const postsCollectionRef = collection(db, "projects", projectId, "posts");
        
        const qPending = query(
            postsCollectionRef,
            where("userId", "==", userId),
            where("status", "in", ["scheduled", "draft", "cancelled"]), 
            orderBy("createdAt", "desc")
        );

        const unsubscribePending = onSnapshot(qPending, (snapshot) => {
            if (isMountedRef.current) {
                const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                posts.sort((a, b) => {
                    const timeA = a.scheduledTime?.toDate()?.getTime() || a.createdAt?.toDate()?.getTime() || 0;
                    const timeB = b.scheduledTime?.toDate()?.getTime() || b.createdAt?.toDate()?.getTime() || 0;
                    return timeA - timeB; // Ascending: Soonest/Oldest-Created first
                });

                setAllPosts(posts);
                setLoadingPosts(false);
            }
        }, (err) => {
            if (isMountedRef.current) {
                setError("Failed to load production queue.");
                setLoadingPosts(false);
            }
        });
        return () => unsubscribePending();
    }, [userId, projectId]);

    // --- 2. Action Handlers ---

    const handleCancelPost = useCallback(async (post) => {
        const confirmed = await confirm({ title: 'Delete Post', message: 'Are you sure you want to delete this post?', confirmLabel: 'Delete', variant: 'danger' });
        if (!confirmed) return;
        setProcessingPostId(post.id);
        try {
            await cancelPostFn({ postId: post.id, projectId: projectId });
            addToast("Post removed from queue.", "info");
        } catch(err) {
            addToast("Action failed. Please try again.", "error");
        } finally {
            setProcessingPostId(null);
        }
    }, [projectId, confirm, addToast]);

    const handlePostNow = useCallback(async (post) => {
        const confirmed = await confirm({ title: 'Publish Now', message: 'Publish this content to Meta immediately?', confirmLabel: 'Publish', variant: 'danger' });
        if (!confirmed) return;
        setProcessingPostId(post.id);
        try {
            const result = await postNowFn({ postId: post.id, projectId });
            if (result.data.success) {
                addToast("Successfully published!", "success");
            } else {
                throw new Error("Meta API returned an error.");
            }
        } catch (err) {
            addToast("Publishing failed. Please try again.", "error");
        } finally {
            setProcessingPostId(null);
        }
    }, [projectId, confirm, addToast]);

    // --- 3. Unified "Ready" Logic ---
    const checkIsPostReady = useCallback((post) => {
        const isMulti = typeof post.content === 'object' && post.content !== null;
        const platforms = isMulti ? Object.keys(post.content) : [post.platform?.toLowerCase() || 'facebook'];
        const isCarousel = post.visualSequence && typeof post.visualSequence === 'object' && !Array.isArray(post.visualSequence);
        
        if (isCarousel) {
            return platforms.every(plat => {
                const platformSlides = post.visualSequence?.[plat];
                if (!platformSlides || !Array.isArray(platformSlides) || platformSlides.length === 0) return false;
                return platformSlides.every(slide => slide.status === 'ready' && slide.visual?.url);
            });
        } else {
            return platforms.every(plat => {
                const v = post.visual?.[plat] || (post.visual?.url ? post.visual : null);
                return !!v?.url && v.status !== 'queued';
            });
        }
    }, []);

    // --- 4. Filtering & Counting ---
    const filteredPosts = useMemo(() => {
        return allPosts.filter(post => {
            if (campaignFilter !== 'all') {
                const matchesCampaign = campaignFilter === 'single' ? !post.streamId : post.streamId === campaignFilter;
                if (!matchesCampaign) return false;
            }
            
            // Logic for Cancelled Posts in Queue
            if (post.status === 'cancelled') {
                const scheduledTime = post.scheduledTime?.toDate ? post.scheduledTime.toDate() : new Date();
                const now = new Date();
                // Show in 'drafts' (Needs Work) ONLY if it's in the future
                if (scheduledTime > now && activeTab === 'drafts') return true;
                return false;
            }

            const isReady = checkIsPostReady(post);
            if (activeTab === 'scheduled') {
                return post.status === 'scheduled' && isReady;
            } else {
                return post.status === 'draft' || (post.status === 'scheduled' && !isReady);
            }
        });
    }, [allPosts, activeTab, campaignFilter, checkIsPostReady]);

    const readyCount = useMemo(() => allPosts.filter(p => p.status === 'scheduled' && checkIsPostReady(p)).length, [allPosts, checkIsPostReady]);
    
    const needsWorkCount = useMemo(() => {
        return allPosts.filter(post => {
            if (post.status === 'cancelled') {
                const scheduledTime = post.scheduledTime?.toDate ? post.scheduledTime.toDate() : new Date();
                return scheduledTime > new Date(); // Only count FUTURE cancelled posts
            }
            // For others (drafts, scheduled-but-not-ready), count them
            if (post.status === 'draft') return true;
            if (post.status === 'scheduled' && !checkIsPostReady(post)) return true;
            return false;
        }).length;
    }, [allPosts, checkIsPostReady]);

    // --- 5. Batch Generation ---
    const handleBatchGenerateVisuals = async () => {
        const draftsToProcess = filteredPosts.filter(post => !checkIsPostReady(post));
        if (draftsToProcess.length === 0) {
            addToast("All drafts in this view already have visuals.", "info");
            return;
        }

        const confirmed = await confirm({ title: 'Batch Generation', message: `Start Batch Generation for ${draftsToProcess.length} posts?`, confirmLabel: 'Start', variant: 'danger' });
        if (!confirmed) return;

        setIsBatchGenerating(true);
        let successCount = 0;
        let failCount = 0;

        for (const post of draftsToProcess) {
            if (!isMountedRef.current) break;
            try {
                setProcessingPostId(post.id);
                const platforms = typeof post.content === 'object' ? Object.keys(post.content) : ['facebook'];
                for (const plat of platforms) {
                    if (!isMountedRef.current) break;
                    if (!post.visual?.[plat]?.url && !post.visual?.url) {
                        await generateImageFromPromptFn({
                            projectId, postId: post.id,
                            prompt: post.visualPrompt || "Professional technological visualization",
                            platform: plat
                        });
                    }
                }
                successCount++;
            } catch (err) {
                failCount++;
            }
        }
        if (!isMountedRef.current) return;
        setIsBatchGenerating(false);
        setProcessingPostId(null);
        if (failCount > 0) {
            addToast(`Batch complete: ${successCount} succeeded, ${failCount} failed.`, "warning");
        } else {
            addToast(`Batch complete. Generated visuals for ${successCount} posts.`, "success");
        }
    };

    return (
        <div className="overflow-hidden h-full flex flex-col">
            
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <nav className="flex space-x-2 bg-warm-dark/30 p-1.5 rounded-2xl border border-white/5">
                    <button
                        onClick={() => setActiveTab('scheduled')}
                        className={`py-2 px-6 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'scheduled' ? 'bg-warm-gray/40 text-slate-blue shadow-glow' : 'text-cream/40 hover:text-cream hover:bg-white/5'}`}
                    >
                        Ready to Publish ({readyCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('drafts')}
                        className={`py-2 px-6 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'drafts' ? 'bg-warm-gray/40 text-slate-blue shadow-glow' : 'text-cream/40 hover:text-cream hover:bg-white/5'}`}
                    >
                        Drafts ({needsWorkCount})
                    </button>
                </nav>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    {activeTab === 'drafts' && campaignFilter !== 'all' && (
                        <button 
                            onClick={handleBatchGenerateVisuals}
                            disabled={isBatchGenerating || filteredPosts.length === 0}
                            className="flex-1 sm:flex-none btn btn-primary px-5 py-2.5 text-xs shadow-glow"
                        >
                            {isBatchGenerating ? <FiRefreshCw className="animate-spin mr-2"/> : <FiPlay className="mr-2"/>}
                            Batch Generate
                        </button>
                    )}

                    <select
                        value={campaignFilter}
                        onChange={(e) => setCampaignFilter(e.target.value)}
                        disabled={loadingPosts}
                        className="form-select text-xs py-2.5 px-4 w-full sm:w-auto font-bold rounded-xl bg-warm-gray/20 text-cream border border-white/10 focus:border-slate-blue focus:ring-2 focus:ring-slate-blue/20 transition-all"
                    >
                        <option value="all">All Production</option>
                        <option value="single">Single Posts Only</option>
                        {!loadingCampaigns && recurringCampaigns?.map(campaign => (
                            <option key={campaign.id} value={campaign.id}>{campaign.streamName}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex-1">
                {error && <p className="text-sm text-soft-coral bg-soft-coral/10 p-4 rounded-2xl border border-soft-coral/20 mb-4 font-medium">{error}</p>}

                {loadingPosts ? (
                    <div className="text-center py-20">
                        <FiLoader className="animate-spin inline-block text-3xl text-slate-blue mb-4"/>
                        <p className="text-cream/40 text-xs font-bold tracking-widest uppercase">Syncing Factory Floor...</p>
                    </div>
                ) : filteredPosts.length === 0 ? (
                    <div className="text-center py-24 bg-warm-dark/30 rounded-3xl border-2 border-dashed border-white/5">
                        <FiZap className="mx-auto h-12 w-12 text-cream/10 mb-4" />
                        <p className="text-cream/40 font-bold uppercase tracking-wide">Your {activeTab} queue is empty.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                        {activeTab === 'drafts' && (() => {
                            const missingVisualCount = filteredPosts.filter(p => p.status !== 'cancelled' && !checkIsPostReady(p)).length;
                            const cancelledCount = filteredPosts.filter(p => p.status === 'cancelled').length;
                            const parts = [];
                            if (missingVisualCount > 0) parts.push(`${missingVisualCount} ${missingVisualCount === 1 ? 'post needs' : 'posts need'} attention before publishing`);
                            if (cancelledCount > 0) parts.push(`${cancelledCount} cancelled`);
                            return parts.length > 0 ? (
                                <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-xs font-bold text-amber-300 uppercase tracking-wider">
                                    {parts.join(' · ')}
                                </div>
                            ) : null;
                        })()}
                        {filteredPosts.map(post => (
                            <DashboardPostCard 
                                key={post.id}
                                post={post}
                                viewMode="queue"
                                onEdit={(p, platform) => onOpenEditModal(p, platform)}
                                onDelete={handleCancelPost}
                                onPostNow={handlePostNow}
                                loadingAction={processingPostId === post.id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

ScheduledPostsList.propTypes = {
    userId: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
    recurringCampaigns: PropTypes.array.isRequired,
    loadingCampaigns: PropTypes.bool.isRequired,
    onOpenEditModal: PropTypes.func.isRequired,
};

export default ScheduledPostsList;